import { useEffect, useState, useMemo } from "react";
import { fnbAPI } from "./fnbApi";
import { toast } from "sonner";
import { Search, Sunrise, Users, Star, Check, AlertTriangle, X, Wrench, Plus, CreditCard, BedDouble, Banknote, Smartphone, Loader2, Receipt } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { NumberInput } from "@/components/ui/number-input";

const MEAL_KIND_LABEL = { room_only: "RO", bb: "BB", hb: "HB", fb: "FB", custom: "Custom" };

/* ── Demo dataset (only when ?demo=1) ──────────────────────────────
 * Realistic populated breakfast register for the marketing shot.
 * Skips every network call — cannot leak into a real hotel.
 */
const DEMO_DATA = {
  kpis: {
    business_date: new Date().toISOString().slice(0, 10),
    expected: 24, served: 15, remaining: 9,
    vip: 4, vip_pending: 1,
    adults_expected: 18, adults_served: 12,
    children_expected: 6,  children_served: 3,
  },
  eligible: [
    { reservation_id: "d-r-01", room_number: "412", guest_name: "Mrs. A. Kapoor",   adults: 2, children: 1, meal_plan_kind: "bb", group_name: null,          status: "waiting", is_vip: true },
    { reservation_id: "d-r-02", room_number: "204", guest_name: "Sharma Family",     adults: 2, children: 2, meal_plan_kind: "bb", group_name: null,          status: "served",  is_vip: false, served_by_email: "priya@hotel.com" },
    { reservation_id: "d-r-03", room_number: "308", guest_name: "R. Mehta",           adults: 1, children: 0, meal_plan_kind: "hb", group_name: null,          status: "served",  is_vip: false, served_by_email: "priya@hotel.com" },
    { reservation_id: "d-r-04", room_number: "515", guest_name: "L. & J. Chen",       adults: 2, children: 0, meal_plan_kind: "bb", group_name: null,          status: "waiting", is_vip: false },
    { reservation_id: "d-r-05", room_number: "102", guest_name: "S. Patel",           adults: 1, children: 0, meal_plan_kind: "bb", group_name: "Corp · TCS",  status: "served",  is_vip: false, served_by_email: "ravi@hotel.com" },
    { reservation_id: "d-r-06", room_number: "620", guest_name: "M. & S. Iyer",       adults: 2, children: 1, meal_plan_kind: "fb", group_name: null,          status: "waiting", is_vip: true },
    { reservation_id: "d-r-07", room_number: "419", guest_name: "Groves Family",      adults: 2, children: 2, meal_plan_kind: "bb", group_name: null,          status: "served",  is_vip: false, served_by_email: "priya@hotel.com" },
    { reservation_id: "d-r-08", room_number: "208", guest_name: "K. Ravindran",       adults: 1, children: 0, meal_plan_kind: "bb", group_name: null,          status: "waiting", is_vip: false },
    { reservation_id: "d-r-09", room_number: "312", guest_name: "Amara Corp · 4 pax", adults: 4, children: 0, meal_plan_kind: "hb", group_name: "Corp · Amara",status: "waiting", is_vip: false },
    { reservation_id: "d-r-10", room_number: "504", guest_name: "Mr. R. Verma",       adults: 1, children: 0, meal_plan_kind: "bb", group_name: null,          status: "served",  is_vip: true, served_by_email: "ravi@hotel.com" },
  ],
};

export default function FnbBreakfast() {
  const [businessDate, setBusinessDate] = useState("");
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [serving, setServing] = useState(null);
  const [repairOpen, setRepairOpen] = useState(false);
  const [walkinOpen, setWalkinOpen] = useState(false);

  const isDemo = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("demo") === "1";

  const load = () => {
    if (isDemo) { setData(DEMO_DATA); return; }
    const params = businessDate ? { business_date: businessDate } : {};
    fnbAPI.breakfast(params).then(setData).catch(() => {});
  };
  useEffect(() => {
    load();
    if (isDemo) return;
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [businessDate]);

  const k = data?.kpis || {};
  const list = data?.eligible || [];

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter(e =>
      (e.room_number || "").toLowerCase().includes(s) ||
      (e.guest_name || "").toLowerCase().includes(s) ||
      (e.group_name || "").toLowerCase().includes(s));
  }, [list, q]);

  return (
    <div className="space-y-5" data-testid="fnb-breakfast-page">
      <div className="on-dark bg-gradient-to-br from-[#0F1530] to-[#070B1F] rounded-2xl p-6 border border-white/10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-300 font-medium flex items-center gap-2">
              <Sunrise className="w-3.5 h-3.5"/> Breakfast Register
              <span className="text-hocp-gold font-semibold tabular-nums" data-testid="fnb-bkfst-bd">{k.business_date || "—"}</span>
            </div>
            <div className="mt-2 font-display text-[36px] font-bold text-white tabular-nums leading-none">
              {k.served || 0} <span className="text-slate-400 text-[22px]">/ {k.expected || 0}</span>
            </div>
            <div className="text-[12px] text-slate-300 mt-1">
              {k.remaining || 0} remaining · {k.vip || 0} VIP ({k.vip_pending || 0} pending)
            </div>
          </div>
          <div className="w-56">
            <DatePicker value={businessDate} onChange={setBusinessDate} data-testid="fnb-bkfst-date"/>
          </div>
          <button
            type="button"
            onClick={() => setRepairOpen(true)}
            data-testid="fnb-bkfst-repair-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/40 text-amber-100 text-[12px] font-medium transition-colors"
            title="A checked-in guest with breakfast missing from this list? Repair their reservation."
          >
            <Wrench className="w-3.5 h-3.5"/>Repair reservation
          </button>
          <button
            type="button"
            onClick={() => setWalkinOpen(true)}
            data-testid="fnb-bkfst-walkin-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/40 text-emerald-100 text-[12px] font-medium transition-colors"
            title="Add breakfast for a guest who didn't include it — charge to room or take payment now."
          >
            <Plus className="w-3.5 h-3.5"/>Walk-in breakfast
          </button>
        </div>
      </div>

      {/* KPIs strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          ["Expected",    k.expected || 0,          "bg-sky-50 text-sky-700"],
          ["Served",      k.served || 0,            "bg-emerald-50 text-emerald-700"],
          ["Remaining",   k.remaining || 0,         "bg-amber-50 text-amber-700"],
          ["VIP Guests",  k.vip || 0,               "bg-fuchsia-50 text-fuchsia-700"],
          ["Adults",      `${k.adults_served||0}/${k.adults_expected||0}`, "bg-slate-50 text-slate-700"],
          ["Children",    `${k.children_served||0}/${k.children_expected||0}`, "bg-slate-50 text-slate-700"],
        ].map(([lab, val, cls]) => (
          <div key={lab} className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className={`w-8 h-8 rounded-lg mb-2 flex items-center justify-center ${cls}`}><Users className="w-4 h-4"/></div>
            <div className="font-display text-[22px] font-semibold text-hocp-navy tabular-nums leading-none">{val}</div>
            <div className="text-[11px] text-slate-500 mt-1.5 uppercase tracking-wider">{lab}</div>
          </div>
        ))}
      </div>

      {/* Search + list */}
      <div className="bg-white border border-slate-200 rounded-2xl">
        <div className="p-3 border-b border-slate-100 relative">
          <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
          <input value={q} onChange={e => setQ(e.target.value)} data-testid="fnb-bkfst-search" aria-label="Search breakfast register by room, guest, or group"
            placeholder="Search by room, guest, or group…"
            className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
        </div>
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2">Room</th>
              <th className="text-left px-4 py-2">Guest</th>
              <th className="text-center px-4 py-2">A / C</th>
              <th className="text-left px-4 py-2">Plan</th>
              <th className="text-left px-4 py-2">Group</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-right px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="py-10 text-center text-[13px] text-slate-400" data-testid="fnb-bkfst-empty">
                <div>No eligible guests match your search.</div>
                <button
                  type="button"
                  onClick={() => setRepairOpen(true)}
                  data-testid="fnb-bkfst-empty-repair"
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 text-[12px] font-medium border border-amber-200"
                >
                  <Wrench className="w-3.5 h-3.5"/>Missing a guest? Repair reservation
                </button>
              </td></tr>
            ) : filtered.map(e => (
              <tr key={e.reservation_id} className={`hover:bg-slate-50 ${e.status === "served" ? "opacity-70" : ""}`} data-testid={`fnb-bkfst-row-${e.room_number || e.reservation_id}`}>
                <td className="px-4 py-2.5 font-mono font-semibold text-hocp-navy">{e.room_number || "—"}</td>
                <td className="px-4 py-2.5 text-slate-800">
                  {e.guest_name || "—"}
                  {e.is_vip && <Star className="inline w-3 h-3 ml-1 text-amber-500 fill-amber-500"/>}
                </td>
                <td className="px-4 py-2.5 text-center tabular-nums text-slate-600">{e.adults}/{e.children}</td>
                <td className="px-4 py-2.5"><span className="inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium bg-violet-100 text-violet-700">{MEAL_KIND_LABEL[e.meal_plan_kind] || e.meal_plan_kind}</span></td>
                <td className="px-4 py-2.5 text-slate-600 text-[12px]">{e.group_name || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-2.5">
                  {e.status === "served" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-emerald-100 text-emerald-700"><Check className="w-3 h-3"/>Served</span>
                  ) : (
                    <span className="inline-flex px-2 py-0.5 rounded-md text-[11px] font-medium bg-amber-100 text-amber-700">Waiting</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right">
                  {e.status === "waiting" ? (
                    <button onClick={() => setServing(e)} data-testid={`fnb-bkfst-serve-${e.room_number || e.reservation_id}`}
                      className="text-[12px] text-emerald-700 hover:text-emerald-900 font-semibold">Mark Served</button>
                  ) : (
                    <span className="text-[11px] text-slate-400">by {(e.served_by_email || "").split("@")[0]}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {serving && <ServeDialog entry={serving} onClose={() => setServing(null)}
        onSaved={() => { setServing(null); load(); }} businessDate={businessDate}/>}
      {repairOpen && <RepairDialog
        onClose={() => setRepairOpen(false)}
        onRepaired={() => { setRepairOpen(false); load(); }}
      />}
      {walkinOpen && <WalkInDialog
        onClose={() => setWalkinOpen(false)}
        onServed={() => { load(); }}
        businessDate={businessDate}
      />}
    </div>
  );
}

function ServeDialog({ entry, onClose, onSaved, businessDate }) {
  const [adults, setAdults] = useState(entry.adults);
  const [children, setChildren] = useState(entry.children);
  const [override, setOverride] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await fnbAPI.serveBreakfast({
        reservation_id: entry.reservation_id,
        stay_id: entry.stay_id || undefined,
        room_number: entry.room_number,
        adults_served: adults, children_served: children,
        business_date: businessDate || undefined,
        override_reason: override || undefined,
      });
      toast.success("Breakfast served");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="fnb-bkfst-serve-dialog">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">Serve Breakfast · Room {entry.room_number}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400"/></button>
        </div>
        <div className="p-6 space-y-3">
          <div className="text-[13px] text-slate-700">{entry.guest_name}</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Adults</label>
              <NumberInput min="0" max={entry.adults} value={adults} onChange={e => setAdults(Number(e.target.value))}
                data-testid="fnb-bkfst-adults" className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Children</label>
              <NumberInput min="0" max={entry.children} value={children} onChange={e => setChildren(Number(e.target.value))}
                data-testid="fnb-bkfst-children" className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"/>
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1">
              <AlertTriangle className="w-3 h-3"/>Override reason (only if re-serving)
            </label>
            <input value={override} onChange={e => setOverride(e.target.value)}
              data-testid="fnb-bkfst-override" placeholder="Leave blank for normal serve"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-[13px] text-slate-600">Cancel</button>
            <button onClick={submit} disabled={busy} data-testid="fnb-bkfst-serve-submit"
              className="px-5 py-2 rounded-lg bg-emerald-600 text-white text-[13px] font-semibold disabled:opacity-60">
              {busy ? "Saving…" : "Mark Served"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function RepairDialog({ onClose, onRepaired }) {
  const [code, setCode] = useState("");
  const [diag, setDiag] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const runDiagnostic = async () => {
    const raw = (code || "").trim();
    if (!raw) { setError("Enter a reservation code (e.g. RES-2026-07-24-403179)"); return; }
    setError(""); setDiag(null); setBusy(true);
    try {
      const d = await fnbAPI.breakfastDiagnostic(raw);
      setDiag(d);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to look up reservation");
    } finally { setBusy(false); }
  };

  const applyRepair = async () => {
    if (!diag) return;
    setBusy(true); setError("");
    try {
      await fnbAPI.repairBreakfast(diag.code || diag.id);
      toast.success(`Breakfast repaired for ${diag.code} — added ${diag.computed_breakfast_dates.length} date(s)`);
      onRepaired();
    } catch (e) {
      setError(e?.response?.data?.detail || "Repair failed");
    } finally { setBusy(false); }
  };

  const alreadyOK = !!diag && (diag.issues || []).length === 0;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="fnb-bkfst-repair-dialog">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy flex items-center gap-2">
            <Wrench className="w-4 h-4 text-amber-600"/>Repair Breakfast for Reservation
          </h3>
          <button onClick={onClose} data-testid="fnb-bkfst-repair-close"><X className="w-5 h-5 text-slate-400"/></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-[12px] text-slate-500 leading-relaxed">
            If a checked-in guest with breakfast is missing from the register — usually because the
            breakfast toggle was <span className="font-semibold">off</span> at check-in — paste the
            reservation code below to inspect and re-enable BB breakfast for every night of their stay.
          </p>

          <div>
            <label htmlFor="fnb-bkfst-repair-code" className="block text-[11.5px] font-medium text-slate-600 mb-1">Reservation code</label>
            <div className="flex gap-2">
              <input
                id="fnb-bkfst-repair-code"
                value={code}
                onChange={e => setCode(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") runDiagnostic(); }}
                data-testid="fnb-bkfst-repair-code"
                placeholder="e.g. RES-2026-07-24-403179"
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"
              />
              <button
                onClick={runDiagnostic}
                disabled={busy}
                data-testid="fnb-bkfst-repair-lookup"
                className="px-4 py-2 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-medium disabled:opacity-60"
              >
                {busy && !diag ? "Looking up…" : "Look up"}
              </button>
            </div>
          </div>

          {error && (
            <div className="text-[12px] text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2"
              data-testid="fnb-bkfst-repair-error">
              {error}
            </div>
          )}

          {diag && (
            <div className="space-y-3 border border-slate-200 rounded-xl p-4 bg-slate-50/60"
              data-testid="fnb-bkfst-repair-diag">
              <div className="grid grid-cols-2 gap-2 text-[12px]">
                <div><span className="text-slate-500">Guest</span><div className="font-semibold text-hocp-navy">{diag.guest_name || "—"}</div></div>
                <div><span className="text-slate-500">Status</span><div className="font-mono text-slate-800">{diag.status}</div></div>
                <div><span className="text-slate-500">Arrival</span><div className="font-mono text-slate-800">{(diag.arrival_date || "").slice(0, 10) || "—"}</div></div>
                <div><span className="text-slate-500">Departure</span><div className="font-mono text-slate-800">{(diag.departure_date || "").slice(0, 10) || "—"}</div></div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Breakfast dates to enable</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {(diag.computed_breakfast_dates || []).map(d => (
                    <span key={d} className="inline-flex px-2 py-0.5 rounded-md text-[11px] font-mono bg-emerald-100 text-emerald-800 border border-emerald-200">{d}</span>
                  ))}
                  {(!diag.computed_breakfast_dates || diag.computed_breakfast_dates.length === 0) && (
                    <span className="text-[12px] text-rose-600">Cannot compute — arrival/departure missing.</span>
                  )}
                </div>
              </div>

              {Array.isArray(diag.persisted_breakfast_dates) && diag.persisted_breakfast_dates.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Currently persisted</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {diag.persisted_breakfast_dates.map((d, idx) => {
                      const dn = String(d).slice(0, 10);
                      const inComputed = (diag.computed_breakfast_dates || []).includes(dn);
                      const cls = inComputed
                        ? "bg-slate-100 text-slate-700 border-slate-200"
                        : "bg-rose-100 text-rose-800 border-rose-200";
                      return (
                        <span key={idx} className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-mono border ${cls}`}>{String(d)}</span>
                      );
                    })}
                  </div>
                </div>
              )}

              {(diag.issues || []).length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-amber-700 font-medium flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3"/>Issues detected
                  </div>
                  <ul className="mt-1 text-[12px] text-slate-700 space-y-0.5 list-disc pl-4">
                    {diag.issues.map((i, idx) => <li key={idx}>{i}</li>)}
                  </ul>
                </div>
              )}

              {alreadyOK && (
                <div className="text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                  Diagnostic looks OK. If the guest still isn&apos;t showing on the register, click
                  <span className="font-semibold"> Force re-apply</span> to rewrite the meal-plan
                  dates from scratch — this fixes edge cases like date-format drift.
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-[13px] text-slate-600">Cancel</button>
            <button
              onClick={applyRepair}
              disabled={busy || !diag || !diag.can_repair}
              data-testid="fnb-bkfst-repair-apply"
              className="px-5 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-[13px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy && diag ? "Repairing…" : (alreadyOK ? "Force re-apply" : "Enable BB Breakfast")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


const PAY_METHODS = [
  { key: "folio", label: "Add to folio", icon: BedDouble, hint: "Post to room · settle at checkout" },
  { key: "cash", label: "Cash", icon: Banknote, hint: "Paid now at restaurant" },
  { key: "card", label: "Card", icon: CreditCard, hint: "Paid now at restaurant" },
  { key: "upi", label: "UPI", icon: Smartphone, hint: "Paid now at restaurant" },
];

function WalkInDialog({ onClose, onServed, businessDate }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [guest, setGuest] = useState(null);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [adultPrice, setAdultPrice] = useState(450);
  const [childPrice, setChildPrice] = useState(225);
  const [method, setMethod] = useState("folio");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const doSearch = async (val) => {
    setQ(val);
    if (!val || val.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try { const d = await fnbAPI.walkinSearch(val.trim()); setResults(d.results || []); }
    catch { /* ignore */ } finally { setSearching(false); }
  };

  const total = (adults * Number(adultPrice || 0) + children * Number(childPrice || 0)).toFixed(2);

  const submit = async () => {
    if (adults + children <= 0) { toast.error("Add at least one breakfast"); return; }
    if (method === "folio" && !guest?.folio_id && !guest?.reservation_id) {
      toast.error("Select an in-house guest to charge to folio"); return;
    }
    setBusy(true);
    try {
      const r = await fnbAPI.walkinServe({
        business_date: businessDate,
        reservation_id: guest?.reservation_id || null,
        folio_id: guest?.folio_id || null,
        room_number: guest?.room_number || null,
        guest_name: guest?.guest_name || "Walk-in guest",
        adults, children,
        adult_price: Number(adultPrice || 0), child_price: Number(childPrice || 0),
        payment_method: method,
        reference_no: reference || null,
      });
      setReceipt(r);
      toast.success("Breakfast recorded");
      onServed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record breakfast");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="fnb-walkin-dialog">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy flex items-center gap-2">
            <Sunrise className="w-4 h-4 text-emerald-600"/>Walk-in Breakfast
          </h3>
          <button onClick={onClose} data-testid="fnb-walkin-close"><X className="w-5 h-5 text-slate-400"/></button>
        </div>

        {receipt ? (
          <div className="p-6 space-y-4" data-testid="fnb-walkin-receipt">
            <div className="flex items-center gap-2 text-emerald-700">
              <Receipt className="w-5 h-5"/><span className="font-semibold text-[15px]">{receipt.receipt_title}</span>
            </div>
            <div className="rounded-xl border border-slate-200 p-4 text-[13px] space-y-1.5 font-mono">
              <div className="flex justify-between"><span className="text-slate-500">Receipt</span><span>{receipt.code}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Guest</span><span>{receipt.guest_name}{receipt.room_number ? ` · Rm ${receipt.room_number}` : ""}</span></div>
              {receipt.items.map((it, i) => (
                <div key={i} className="flex justify-between"><span className="text-slate-500">{it.label} × {it.qty}</span><span>{receipt.currency} {it.amount}</span></div>
              ))}
              <div className="flex justify-between border-t border-slate-200 pt-1.5 font-semibold text-hocp-navy"><span>Total</span><span>{receipt.currency} {receipt.amount}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Settlement</span><span className="uppercase">{receipt.payment_method === "folio" ? "Room Account" : receipt.payment_method}</span></div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setReceipt(null); setGuest(null); setQ(""); setResults([]); setAdults(1); setChildren(0); }}
                className="px-4 py-2 text-[13px] text-slate-600" data-testid="fnb-walkin-another">Add another</button>
              <button onClick={() => window.open(`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/fnb/v1/breakfast/walkin/${receipt.id}/receipt.html`, "_blank")}
                className="px-4 py-2 rounded-lg border border-emerald-600 text-emerald-700 text-[13px] font-semibold inline-flex items-center gap-1.5" data-testid="fnb-walkin-print">
                <Receipt className="w-3.5 h-3.5"/>Print receipt</button>
              <button onClick={onClose} className="px-5 py-2 rounded-lg bg-emerald-600 text-white text-[13px] font-semibold" data-testid="fnb-walkin-done">Done</button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {/* 1 · Find guest */}
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">1 · Find guest (name or room)</label>
              <div className="mt-1 relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
                <input value={q} onChange={e => doSearch(e.target.value)} placeholder="e.g. Aarav or 101" aria-label="Find guest by name or room"
                  data-testid="fnb-walkin-search" autoFocus
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
              </div>
              {q.length >= 2 && (
                <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-slate-100 divide-y divide-slate-50">
                  {searching ? <div className="p-3 text-[12px] text-slate-400 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin"/>Searching…</div>
                   : results.length === 0 ? <div className="p-3 text-[12px] text-slate-400">No in-house guests match</div>
                   : results.map((r) => (
                    <button key={r.reservation_id} onClick={() => { setGuest(r); setResults([]); setQ(`${r.guest_name} · Rm ${r.room_number || "—"}`); }}
                      data-testid={`fnb-walkin-guest-${r.reservation_id}`}
                      className={`w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-center justify-between ${guest?.reservation_id === r.reservation_id ? "bg-emerald-50" : ""}`}>
                      <span className="text-[13px] text-slate-700">{r.guest_name} <span className="text-slate-400">· Rm {r.room_number || "—"}</span></span>
                      {r.breakfast_included && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">already incl.</span>}
                    </button>
                  ))}
                </div>
              )}
              {guest && (
                <div className="mt-2 text-[12px] text-emerald-700 flex items-center gap-1"><Check className="w-3.5 h-3.5"/>{guest.guest_name} · Room {guest.room_number || "—"}{guest.folio_id ? "" : " (no open folio)"}</div>
              )}
            </div>

            {/* 2 · Breakfast qty + price */}
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">2 · Breakfasts</label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {[["Adults", adults, setAdults, adultPrice, setAdultPrice, "adults"],
                  ["Children", children, setChildren, childPrice, setChildPrice, "children"]].map(([lbl, qty, setQty, price, setPrice, tid]) => (
                  <div key={tid} className="rounded-lg border border-slate-200 p-2">
                    <div className="text-[11px] text-slate-500 mb-1">{lbl}</div>
                    <NumberInput min="0" value={qty} onChange={e => setQty(Math.max(0, Number(e.target.value)))}
                      data-testid={`fnb-walkin-${tid}`} aria-label={`${lbl} — quantity`} className="w-full px-2 py-1.5 border border-slate-200 rounded text-[13px] font-mono mb-1"/>
                    <div className="flex items-center gap-1">
                      <span className="text-[11px] text-slate-400">₹</span>
                      <NumberInput min="0" value={price} onChange={e => setPrice(e.target.value)}
                        data-testid={`fnb-walkin-${tid}-price`} aria-label={`${lbl} — unit price`} className="w-full px-2 py-1 border border-slate-200 rounded text-[12px] font-mono" title="Unit price"/>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 3 · Payment */}
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">3 · Payment</label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {PAY_METHODS.map((m) => {
                  const Icon = m.icon; const active = method === m.key;
                  return (
                    <button key={m.key} onClick={() => setMethod(m.key)} data-testid={`fnb-walkin-pay-${m.key}`}
                      className={`text-left px-3 py-2 rounded-lg border transition-colors ${active ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className="flex items-center gap-1.5 text-[13px] font-medium text-slate-700"><Icon className="w-3.5 h-3.5"/>{m.label}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{m.hint}</div>
                    </button>
                  );
                })}
              </div>
              {method !== "folio" && (
                <div className="mt-2">
                  <label htmlFor="fnb-walkin-reference" className="block text-[11.5px] font-medium text-slate-600 mb-1">Reference / txn no. <span className="text-slate-400 font-normal">(optional)</span></label>
                  <input id="fnb-walkin-reference" value={reference} onChange={e => setReference(e.target.value)} placeholder="e.g. UPI ref"
                    data-testid="fnb-walkin-reference" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <div className="text-[15px] font-semibold text-hocp-navy" data-testid="fnb-walkin-total">Total ₹ {total}</div>
              <button onClick={submit} disabled={busy || (adults + children) <= 0} data-testid="fnb-walkin-submit"
                className="px-5 py-2 rounded-lg bg-emerald-600 text-white text-[13px] font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Check className="w-3.5 h-3.5"/>}
                {method === "folio" ? "Charge to room" : "Take payment"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
